import clsx from "clsx";
import { useEffect, useState } from "react";
import { CalculatorAnswerTypes } from "../../../../types/Co2CalculatorAnswerTypes";
import { ArrayElement } from "../../../../types/util";
import { calculatorActions } from "../../../redux/co2calculatorSlice";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  getValueFromAnswerValue,
  usePrismaTranslations,
} from "../../../util/calculator";
import { TSelectCurrentQuestion } from "../../../util/prismaHelper";
import Input from "../../Input/Input";
import BasicRangeInput from "../../Input/Range/BasicRangeInput";
import styles from "./RangeAnswer.module.scss";

export type RangeAnswerProps = {
  answer: ArrayElement<TSelectCurrentQuestion["QAnswer"]>;
  qId: string;
};

function RangeAnswer({ answer, qId }: RangeAnswerProps) {
  const tPrisma = usePrismaTranslations(answer.QAnswerLabelTranslation);
  const dispatch = useAppDispatch();
  const calcState = useAppSelector((state) => state.calculator);
  const transport4AnswerData = useAppSelector(
    (state) => state.calculator.answers["transport@4"]
  );
  const pet1AnswerData = useAppSelector(
    (state) => state.calculator.answers["pet@1"]
  );
  const initialState = () =>
    (answer.data as CalculatorAnswerTypes.RangeOption[]).reduce((acc, curr) => {
      let a = calcState.answers[qId]?.value;
      if (typeof a === "object") {
        a = a[curr.key];
      }
      if (typeof a === "string") {
        a = parseInt(a);
      }
      if (!a || isNaN(a)) a = 0;
      acc[curr.key] = a;
      return acc;
    }, {} as Record<string, number>);
  const [state, setState] = useState<Record<string, number>>(initialState());

  useEffect(() => {
    if (transport4AnswerData) {
      if (
        getValueFromAnswerValue(transport4AnswerData.value) === "no_details"
      ) {
        setState(initialState());
      }
    }
  }, [transport4AnswerData]);

  useEffect(() => {
    if (pet1AnswerData) {
      if (getValueFromAnswerValue(pet1AnswerData.value) === "no_details") {
        setState(initialState());
      }
    }
  }, [pet1AnswerData]);

  if (!answer.data) return null;

  const onSave = (
    key: string,
    value: number | string,
    _d: CalculatorAnswerTypes.RangeOption
  ) => {
    dispatch(
      calculatorActions.asyncUpdateAnswer({
        qId,
        qaId: answer.id,
        value: {
          value: { [key]: value },
          _meta: {
            [answer.id]: {
              ..._d,
              category: calcState.currentQuestion!.category,
            },
          },
        },
      })
    );
  };

  const onChange = (
    key: string,
    value: number | string,
    d: CalculatorAnswerTypes.RangeOption
  ) => {
    process.env.NODE_ENV !== "production" &&
      console.log("onChange", key, value, d);
    if (typeof value === "string") {
      setState((s) => ({ ...s, [key]: parseInt(value) }));
    } else {
      setState((s) => ({ ...s, [key]: value }));
    }
    dispatch(
      calculatorActions.updateData({
        questionId: qId + "_" + key,
        value:
          (d.factor ?? 1) *
          (typeof value === "string" ? parseInt(value) : value),
        data: d,
        category: calcState.currentQuestion!.category,
      })
    );
  };

  return (
    <div className={styles.container}>
      {((answer.data as CalculatorAnswerTypes.RangeOption[]) || []).map(
        (data) => {
          if (data.type === "header") {
            return (
              <div key={data.key} className={styles.label}>
                <div className={clsx(styles.header, styles.title)}>
                  {tPrisma.label(data) ?? data.key}
                  {data.hint && (
                    <div className={styles.hint}>{tPrisma.hint(data)}</div>
                  )}
                </div>
              </div>
            );
          }
          return (
            <label key={data.key}>
              <div className={styles.label}>
                <div className={styles.header}>
                  <div className={styles.title}>
                    {tPrisma.label(data) ?? data.key}
                  </div>
                  {data.hint && (
                    <div className={styles.hint}>{tPrisma.hint(data)}</div>
                  )}
                </div>
                {data.type !== "input" && (
                  <div className={styles.value}>{state[data.key]}</div>
                )}
              </div>
              {data.type === "input" ? (
                <Input
                  inputProps={{
                    type: "number",
                    value: state[data.key],
                    min: 0,
                    onChange: (e) => onChange(data.key, e.target.value, data),
                    onBlur: (e) => onSave(data.key, e.target.value, data),
                  }}
                  inputContainerClassName={styles.customInput}
                />
              ) : (
                <BasicRangeInput
                  className={styles.rangeInput}
                  name={data.key}
                  value={state[data.key]}
                  onChange={(v) => onChange(data.key, v, data)}
                  onSave={(v) => onSave(data.key, v, data)}
                  min={data.min}
                  max={data.max}
                  step={data.step}
                  style={{
                    "--range": "calc(" + (data.max ?? 20) + ")",
                    "--ratio":
                      "calc((" + (state[data.key] ?? 0) + ") / var(--range))",
                  }}
                />
              )}
            </label>
          );
        }
      )}
    </div>
  );
}

export default RangeAnswer;
